import React, { useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useLanguage } from "../LanguageContext";
import { AppIcon } from "./icons";
import {
  HomepageHeader,
  HomepageHero,
  // HomepageSampleTranscript intentionally not imported — see the
  // 2026-09-27 comment above its definition in HomepageSections.js. The
  // real-UI hero below already shows this exact demo exchange, so rendering
  // it again here was redundant.
  HomepageHowItWorks,
  HomepageWhyThisWorks,
  HomepageEligibility,
  HomepageWhatYouGet,
  HomepageCtaBand,
  HomepageFooter,
} from "./HomepageSections";

const COUNTRIES = [
  "Spain", "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia",
  "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada",
  "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia",
  "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati",
  "North Korea", "South Korea", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger",
  "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Sudan", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan",
  "Tajikistan", "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

// ─── Wizard: progress dots ─────────────────────────────────────────────────
// Same pattern as OnboardingModal's ProgressDots (not exported there, so
// duplicated here rather than imported). Current step dot is larger/filled,
// inactive dots are smaller/gray. No animation beyond the existing color/size
// transition — avoids motion issues for older users.

function ProgressDots({ total, current }) {
  return (
    <div className="flex items-center justify-center gap-3" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`block rounded-full transition-all duration-200 ${
            i === current
              ? "w-4 h-4 bg-indigo-600"
              : "w-3 h-3 bg-ink-400"
          }`}
        />
      ))}
    </div>
  );
}

// ─── Wizard: step content ───────────────────────────────────────────────────
// Each step is a stateless component — no local state of its own, every
// value/setter is passed down from OnboardingScreen's top-level state so
// Back never loses what the user already typed. Mirrors the OnboardingModal
// StepWelcome/StepHowItWorks/StepReady pattern.
//
// Copy note: step headings (auth.step1Title..step4Title) are plain-language
// drafts, not final copy — flagged for a copy pass, not locked.

function SignupStepAccount({ t, email, setEmail, password, setPassword, invitationCode, setInvitationCode }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center text-center gap-3 mb-2">
        <div className="w-20 h-20 bg-indigo-100 rounded-card flex items-center justify-center">
          <AppIcon />
        </div>
        <h2 className="text-3xl font-bold text-ink-900 leading-tight">{t('auth.step1Title')}</h2>
      </div>
      <input type="email" placeholder={t('auth.email')} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-5 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500 text-lg" required />
      <input type="password" placeholder={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-5 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500 text-lg" required />
      <input type="text" placeholder={t('auth.invitationCode')} value={invitationCode} onChange={(e) => setInvitationCode(e.target.value.toUpperCase())} className="w-full px-5 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500 text-lg font-mono tracking-wider" required />
      <p className="text-sm text-ink-600 -mt-2">
        {t('auth.noCode')} <a href="mailto:bernardm@ucm.es?subject=Invitation%20Code%20Request" className="text-indigo-600 underline hover:text-indigo-800 font-semibold">{t('auth.requestCode')}</a>
      </p>
    </div>
  );
}

function SignupStepAbout({ t, name, setName, surname, setSurname, age, setAge, country, setCountry }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-3xl font-bold text-ink-900 leading-tight text-center mb-2">{t('auth.step2Title')}</h2>
      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder={t('auth.firstName')} value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" required />
        <input type="text" placeholder={t('auth.lastName')} value={surname} onChange={(e) => setSurname(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" required />
      </div>
      <input type="number" placeholder={t('auth.age')} value={age} onChange={(e) => setAge(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" />
      <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" required>
        <option value="">{t('auth.selectCountry')}</option>
        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

function SignupStepEnglish({ t, nativeLanguage, setNativeLanguage, englishLevel, setEnglishLevel, studyMethod, setStudyMethod, institutionName, setInstitutionName }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-3xl font-bold text-ink-900 leading-tight text-center mb-2">{t('auth.step3Title')}</h2>
      <select value={nativeLanguage} onChange={(e) => setNativeLanguage(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" required>
        <option value="">{t('auth.selectLanguage')}</option>
        {['Spanish', 'Portuguese', 'French', 'German', 'Italian', 'Mandarin', 'Japanese', 'Korean', 'Arabic', 'Russian', 'Hindi', 'Other'].map(lang => (
          <option key={lang} value={lang}>{t(`languages.${lang}`)}</option>
        ))}
      </select>
      <select value={englishLevel} onChange={(e) => setEnglishLevel(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500">
        {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(level => (
          <option key={level} value={level}>{t(`levels.${level}`)}</option>
        ))}
      </select>
      <select value={studyMethod} onChange={(e) => setStudyMethod(e.target.value)} className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500" required>
        <option value="">{t('auth.studyMethod')}</option>
        {['Academy', 'App', 'Self-study', 'Private tutor', 'Other'].map(method => (
          <option key={method} value={method}>{t(`studyMethods.${method}`)}</option>
        ))}
      </select>
      {(studyMethod === 'Academy' || studyMethod === 'App' || studyMethod === 'Private tutor' || studyMethod === 'Other') && (
        <input
          type="text"
          placeholder={studyMethod === 'Academy' ? t('auth.academyName') : studyMethod === 'App' ? t('auth.appName') : studyMethod === 'Private tutor' ? t('auth.tutorName') : t('auth.pleaseSpecify')}
          value={institutionName}
          onChange={(e) => setInstitutionName(e.target.value)}
          className="w-full px-4 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500"
          required={studyMethod === 'Academy' || studyMethod === 'App'}
        />
      )}
    </div>
  );
}

function SignupStepConsent({ t, consentChecked, setConsentChecked }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-3xl font-bold text-ink-900 leading-tight text-center mb-2">{t('auth.step4Title')}</h2>
      {/* Consent for audio-recording in a UCM-ethics-approved study — the
          checkbox MUST render unchecked by default (see useState(false) in
          OnboardingScreen) and MUST keep gating the submit button in the
          wizard footer. Do not change this default or the disabled logic. */}
      <div className="p-4 rounded-card border-2 border-info-600/30 bg-info-100">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-1 w-6 h-6 rounded border-2 border-ink-400 accent-indigo-600 flex-shrink-0 cursor-pointer"
            aria-required="true"
          />
          <span className="text-base text-ink-900 leading-relaxed">
            <strong className="block mb-2 text-ink-900">{t('auth.consentTitle')}</strong>
            <ul className="list-disc ml-4 space-y-1 mb-2">
              <li>{t('auth.consentItem1')}</li>
              <li>{t('auth.consentItem2')}</li>
              <li>{t('auth.consentItem3')}</li>
              <li>{t('auth.consentItem4')}</li>
            </ul>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-700 underline font-semibold hover:text-indigo-900"
              onClick={(e) => e.stopPropagation()}
            >
              {t('auth.consentPrivacyLink')}
            </a>
          </span>
        </label>
      </div>
      {!consentChecked && (
        <p className="text-center text-base text-ink-600" role="status">
          {t('auth.consentRequired')}
        </p>
      )}
    </div>
  );
}

const SIGNUP_TOTAL_STEPS = 4;

export function OnboardingScreen({ onStart }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [message, setMessage] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [validatingCode, setValidatingCode] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [premiumDays, setPremiumDays] = useState(0);
  const { t, language, setLanguage } = useLanguage();

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [age, setAge] = useState('');
  const [nativeLanguage, setNativeLanguage] = useState('');
  const [country, setCountry] = useState('');
  const [englishLevel, setEnglishLevel] = useState('B1');
  const [studyMethod, setStudyMethod] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  // Signup wizard step (4 steps: Account / About you / Your English study /
  // Consent & finish). Login mode doesn't use this — it stays a single
  // simple 2-field form. All field state above stays lifted at this
  // component level so Back never loses what the user already entered.
  const [step, setStep] = useState(0);
  const signupFormRef = useRef(null);

  const isFirstSignupStep = step === 0;
  const isLastSignupStep = step === SIGNUP_TOTAL_STEPS - 1;

  // Steps 1-3's "Next" validates only the currently-mounted step's fields —
  // since only step `steps[step]`'s inputs exist in the DOM at any time,
  // the native reportValidity() call on the form naturally scopes to them.
  const handleNextStep = () => {
    if (signupFormRef.current && !signupFormRef.current.reportValidity()) {
      return;
    }
    setStep((s) => Math.min(s + 1, SIGNUP_TOTAL_STEPS - 1));
  };

  const handleBackStep = () => {
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleToggleMode = () => {
    setIsSignUp((prev) => !prev);
    setStep(0);
    setMessage('');
  };

  const handleAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');

    if (isSignUp) {
      if (!invitationCode.trim()) {
        setMessage('Please enter an invitation code to sign up.');
        setStep(0);
        setLoading(false);
        return;
      }

      setValidatingCode(true);

      const { data: validationData, error: validationError } = await supabase.rpc(
        'validate_invitation_code',
        { code_input: invitationCode.trim() }
      );

      setValidatingCode(false);

      if (validationError) {
        setMessage('Error validating invitation code: ' + validationError.message);
        setStep(0);
        setLoading(false);
        return;
      }

      if (!validationData.valid) {
        setMessage(validationData.error || 'Invalid invitation code. Please check and try again.');
        setStep(0);
        setLoading(false);
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Demographic fields collected during signup. Supabase stores this
          // object as `raw_user_meta_data` on the new auth.users row. The
          // `handle_new_user()` DB trigger (SECURITY DEFINER, bypasses RLS)
          // reads it from there and copies an allowlisted subset into the
          // new profiles row. We can no longer write these via a direct
          // client-side upsert to `profiles` because `anon` has zero access
          // to that table and the browser is still anonymous at this point
          // (email confirmation hasn't completed yet).
          data: {
            name,
            surname,
            age: age ? parseInt(age) : null,
            native_language: nativeLanguage,
            country,
            english_level: englishLevel,
            study_method: studyMethod,
            institution_name: institutionName || null
          }
        }
      });

      if (authError) {
        setMessage(authError.message);
        setStep(0);
        setLoading(false);
        return;
      }

      if (authData.user) {
        const { data: useCodeData, error: useCodeError } = await supabase.rpc(
          'use_invitation_code',
          {
            code_input: invitationCode.trim(),
            user_id_input: authData.user.id
          }
        );

        if (useCodeError) {
          setMessage('Account created but failed to apply invitation code. Please contact support.');
          setStep(0);
          setLoading(false);
          return;
        }

        if (!useCodeData.success) {
          setMessage('Account created but invitation code could not be applied: ' + useCodeData.error);
          setStep(0);
          setLoading(false);
          return;
        }

        // Demographic fields are populated server-side by the handle_new_user
        // trigger from the signUp() metadata above (see the options.data
        // block), not by a client upsert here. This session is still
        // anonymous/unconfirmed and `anon` has no access to `profiles`, so a
        // direct upsert from here would always fail (previously it did so
        // silently, logged to console).

        setPremiumDays(useCodeData.grants_premium ? useCodeData.premium_duration_days : 0);
        setShowConfirmation(true);
        setLoading(false);
        return;
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setMessage(error.message);
      } else {
        setMessage('Logged in successfully!');
      }
    }

    setLoading(false);
  };

  // Marketing-page navigation helpers — jump to the real sign-in/sign-up
  // form below and, for the two "start practicing" CTAs, flip into signup
  // mode first. This only ever calls the same setIsSignUp toggle the form's
  // own "Need an account? Sign Up" button already exposes — no new auth
  // behavior, just wiring existing state from the new marketing sections.
  const scrollToSignIn = () => {
    document.getElementById('signin')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleRequestInvitation = () => {
    setIsSignUp(true);
    setStep(0);
    scrollToSignIn();
  };
  const handleLogInNavClick = () => {
    setIsSignUp(false);
    setStep(0);
    scrollToSignIn();
  };

  if (showConfirmation) {
    const steps = [t('confirmation.step1'), t('confirmation.step2'), t('confirmation.step3'), t('confirmation.step4')];
    return (
      <div className="bg-paper-50 text-ink-900 min-h-screen flex flex-col justify-center items-center text-center p-8">
        <div className="max-w-2xl w-full bg-paper-100 rounded-modal shadow-2xl p-12 animate-check-in">
          <div className="mx-auto w-32 h-32 mb-8 bg-indigo-100 rounded-full flex items-center justify-center">
            <svg className="w-16 h-16 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-5xl font-extrabold mb-6 text-indigo-700">{t('confirmation.title')}</h1>
          <p className="text-2xl text-ink-600 mb-8 leading-relaxed">
            {t('confirmation.sentTo')} <strong className="text-indigo-600">{email}</strong>
          </p>

          <div className="bg-info-100 border-2 border-info-600/30 rounded-card p-6 mb-8 text-left">
            <p className="text-lg text-ink-900 font-semibold mb-4">{t('confirmation.nextSteps')}</p>
            <ol className="text-lg text-ink-600 space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-info-600 text-white text-sm font-bold grid place-items-center mt-0.5">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {premiumDays > 0 && (
            <div className="bg-rose-100 border-2 border-rose-600/40 rounded-card p-6 mb-8">
              <p className="text-xl font-bold text-rose-800">
                {t('confirmation.premiumDays', { days: premiumDays })}
              </p>
            </div>
          )}

          <p className="text-ink-600 text-sm mb-6">{t('confirmation.noEmail')}</p>

          <button
            onClick={() => {
              setShowConfirmation(false);
              setIsSignUp(false);
              setStep(0);
            }}
            className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xl font-bold rounded-control shadow-lg transition"
          >
            {t('confirmation.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper-50 text-ink-900 min-h-screen">
      <HomepageHeader t={t} language={language} setLanguage={setLanguage} onLogInClick={handleLogInNavClick} />
      <HomepageHero t={t} onRequestInvitation={handleRequestInvitation} />
      <HomepageHowItWorks t={t} />
      <HomepageWhyThisWorks t={t} />
      <HomepageEligibility t={t} />
      <HomepageWhatYouGet t={t} />
      <HomepageCtaBand t={t} />

      {/* --- Real sign-in / sign-up form. Login mode (the `!isSignUp` branch
          below) is untouched auth logic/JSX — only the layout wrapper
          around it changed (was a full-screen centered card, now a section
          of the homepage). Signup mode is the new 4-step wizard; handleAuth
          itself (RPC/signUp call sequence) is unchanged and still only
          fires once, from this wizard's step-4 submit button. --- */}
      <div id="signin" className="flex flex-col justify-center items-center text-center p-8">
      <div className="max-w-md w-full">

        {!isSignUp ? (
          <>
            <div className="mx-auto w-24 h-24 mb-6 bg-indigo-100 rounded-card flex items-center justify-center">
              <AppIcon />
            </div>
            <h1 className="text-4xl font-bold mb-4">{t('auth.welcome')}</h1>
            <p className="text-xl text-ink-600 mb-8">{t('auth.subtitle')}</p>

            <form onSubmit={handleAuth} className="flex flex-col gap-4 text-left">
              <input type="email" placeholder={t('auth.email')} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-5 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500 text-lg" required />
              <input type="password" placeholder={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-5 py-3 rounded-control border border-ink-200 placeholder:text-ink-600 focus:border-indigo-500 text-lg" required />

              <button
                type="submit"
                className={`w-full text-xl font-semibold py-5 px-8 rounded-control shadow-lg transition ${
                  isSignUp && !consentChecked
                    ? 'bg-ink-200 text-ink-600 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
                disabled={loading || (isSignUp && !consentChecked)}
              >
                {loading ? t('auth.loading') : (isSignUp ? t('auth.signUp') : t('auth.logIn'))}
              </button>
              <button type="button" onClick={handleToggleMode} className="w-full text-lg font-semibold text-indigo-600 py-3">
                {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.needAccount')}
              </button>
            </form>
            {message && <p className="mt-4 text-danger-600 text-lg">{message}</p>}
          </>
        ) : (
          <>
            {/* 4-step signup wizard card shell — reuses OnboardingModal's
                card tokens (rounded-modal, shadow-2xl, paper-100), the
                step-counter text above ProgressDots, the Back/Next sticky
                footer (spacer instead of Back on step 1, autoFocus on the
                primary button), aria-live="polite" on the content region,
                and no step-transition animation. Rendered inline here at
                #signin, not as a position:fixed overlay, so scrollToSignIn()
                keeps working. max-h uses dvh + overflow-y-auto (not a fixed
                pixel height) so long steps stay reachable at 200% zoom. */}
            <div className="bg-paper-100 rounded-modal shadow-2xl w-full flex flex-col text-left">
              <form ref={signupFormRef} onSubmit={handleAuth} className="flex flex-col">
                <div
                  className="min-h-0 max-h-[65dvh] overflow-y-auto px-6 pt-10 pb-6 sm:px-8 sm:pt-12"
                  aria-live="polite"
                >
                  {step === 0 && (
                    <SignupStepAccount
                      t={t}
                      email={email} setEmail={setEmail}
                      password={password} setPassword={setPassword}
                      invitationCode={invitationCode} setInvitationCode={setInvitationCode}
                    />
                  )}
                  {step === 1 && (
                    <SignupStepAbout
                      t={t}
                      name={name} setName={setName}
                      surname={surname} setSurname={setSurname}
                      age={age} setAge={setAge}
                      country={country} setCountry={setCountry}
                    />
                  )}
                  {step === 2 && (
                    <SignupStepEnglish
                      t={t}
                      nativeLanguage={nativeLanguage} setNativeLanguage={setNativeLanguage}
                      englishLevel={englishLevel} setEnglishLevel={setEnglishLevel}
                      studyMethod={studyMethod} setStudyMethod={setStudyMethod}
                      institutionName={institutionName} setInstitutionName={setInstitutionName}
                    />
                  )}
                  {step === 3 && (
                    <SignupStepConsent
                      t={t}
                      consentChecked={consentChecked} setConsentChecked={setConsentChecked}
                    />
                  )}
                </div>

                <div className="shrink-0 border-t border-ink-200 bg-paper-100 px-6 py-5 sm:px-8 sm:rounded-b-modal flex flex-col gap-3">
                  <p className="text-center text-base font-medium text-ink-600">
                    {t('onboarding.stepCounter', { current: step + 1, total: SIGNUP_TOTAL_STEPS })}
                  </p>
                  <ProgressDots total={SIGNUP_TOTAL_STEPS} current={step} />

                  <div className="flex items-center gap-3">
                    {!isFirstSignupStep ? (
                      <button
                        type="button"
                        onClick={handleBackStep}
                        className="flex-1 py-4 rounded-control border-2 border-ink-200 text-ink-600 text-xl font-semibold hover:bg-paper-50 transition-colors"
                      >
                        {t('common.back')}
                      </button>
                    ) : (
                      /* Invisible spacer keeps the primary button right-aligned on Step 1 */
                      <div className="flex-1" aria-hidden="true" />
                    )}

                    {!isLastSignupStep ? (
                      <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex-1 py-4 rounded-control bg-indigo-600 hover:bg-indigo-700 text-white text-xl font-bold transition-colors shadow-lg"
                        autoFocus
                      >
                        {t('common.next')}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className={`flex-1 py-4 rounded-control text-xl font-bold shadow-lg transition-colors ${
                          isSignUp && !consentChecked
                            ? 'bg-ink-200 text-ink-600 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                        disabled={loading || (isSignUp && !consentChecked)}
                        autoFocus
                      >
                        {loading ? t('auth.loading') : (isSignUp ? t('auth.signUp') : t('auth.logIn'))}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <button type="button" onClick={handleToggleMode} className="w-full text-lg font-semibold text-indigo-600 py-3 mt-4">
              {t('auth.alreadyHaveAccount')}
            </button>
            {message && <p className="mt-4 text-danger-600 text-lg">{message}</p>}
          </>
        )}

      </div>
      </div>
      {/* --- End of unchanged auth form --- */}

      <HomepageFooter t={t} />
    </div>
  );
}
